import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, AlertCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePortfolioMetrics } from "@/hooks/usePortfolioMetrics";
import { useBalanceTrend } from "@/hooks/useBalanceTrend";
import { useCurrencyConversion } from "@/hooks/useCurrencyConversion";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import { PortfolioSummaryCard } from "@/components/account-overview/PortfolioSummaryCard";
import { PortfolioBalanceTrendChart } from "@/components/account-overview/PortfolioBalanceTrendChart";
import { AccountDistributionChart } from "@/components/account-overview/AccountDistributionChart";
import { AccountListByType } from "@/components/account-overview/AccountListByType";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorFallback } from "@/components/ErrorFallback";
import type { TimePeriod } from "@/types/account-drilldown";
import {
  useCreateAccount,
  useUpdateAccount,
  useDeleteAccount,
} from "@accountHooks";
import { useCreateBankAccountWithDetails } from "@accountHooks";
import { useFetchAllBanks } from "@accountHooks";
import {
  AccountForm,
  AccountFormData,
} from "@/components/account-form/AccountForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {Account} from "@/integrations/supabase/account.models.ts";
import {camelCaseObjectKeys} from "@lib/utils.ts";

function AccountOverviewContent() {
  const [timePeriod] = useState<TimePeriod>("last30days");
  const [editing, setEditing] = useState<Account | undefined>();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const createBankAccountWithDetails = useCreateBankAccountWithDetails();
  const { data: banks = [] } = useFetchAllBanks();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  // TODO: Get base currency from user settings
  const baseCurrency = "USD";

  // Fetch portfolio metrics
  const {
    metrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = usePortfolioMetrics(baseCurrency);

  // Initialize currency conversion hook
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedAccounts,
    totalNetWorth,
    isConverting,
    conversionError,
    accountColors,
  } = useCurrencyConversion({
    baseCurrency,
    accounts: (metrics?.accountsWithConvertedBalances || []) as any,
  });

  // Fetch portfolio balance trend
  const { data: balanceTrend, isLoading: trendLoading } = useBalanceTrend(
    "portfolio",
    timePeriod,
    baseCurrency,
  );

  // Handle add account action
  const handleAddAccount = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const openEdit = (account: Account) => {
    setEditing(account);
    setDialogOpen(true);
  };

  const handleSave = async (data: AccountFormData) => {
    try {
      if (editing) {
        await updateAccount.mutateAsync({ id: editing.id, ...data });
        toast.success("Account updated");
      } else if (data.type === "bank") {
        // Look up the bank by name to get its ID (bank may have been created via AccountForm's onCreate)
        const existingBank = banks.find((b) => b.name === data.bank_name);
        await createBankAccountWithDetails.mutateAsync({
          accountData: {
            name: data.name,
            nickname: data.nickname,
            account_number: data.account_number,
            type: data.subtype,
            currency: data.currency,
            balance: data.balance,
            status: data.status,
          },
          bankId: existingBank?.id,
          bankName: existingBank ? undefined : data.bank_name,
          bankAddress: data.bank_address,
          sortCode: data.sort_code,
          iban: data.iban,
          swiftCode: data.swift_code,
          routingNumber: data.routing_number,
          transitNumber: data.transit_number,
          institutionNumber: data.institution_number,
        });
        toast.success("Account created");
      } else {
        const request = camelCaseObjectKeys(data);
        await createAccount.mutateAsync(request);
        toast.success("Account created");
      }
      setDialogOpen(false);
      setEditing(undefined);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAccount.mutateAsync(id);
      toast.success("Account deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Map accounts to include converted balances
  const accountsByTypeWithConverted = useMemo(() => {
    return Object.fromEntries(
      Object.entries(metrics?.accountsByType || {}).map(([type, accounts]) => [
        type,
        accounts.map((account) => {
          const withConverted = convertedAccounts.find(
            (a) => a.id === account.id,
          );
          return (
            withConverted || {
              ...account,
              convertedBalance: account.balance,
              conversionRate: 1,
            }
          );
        }),
      ]),
    );
  }, [metrics?.accountsByType, convertedAccounts]);

  const filteredAccountsByType = useMemo(() => {
    const filtered: Record<string, any[]> = {};
    const query = searchQuery.toLowerCase().trim();

    Object.entries(accountsByTypeWithConverted).forEach(([type, accounts]) => {
      // Filter by type if not 'all'
      if (typeFilter !== "all" && type !== typeFilter) return;

      // Filter by search query
      const matchingAccounts = accounts.filter(
        (account) =>
          account.name.toLowerCase().includes(query) ||
          account.nickname?.toLowerCase().includes(query) ||
          account.account_number?.toLowerCase().includes(query),
      );

      if (matchingAccounts.length > 0) {
        filtered[type] = matchingAccounts;
      }
    });

    return filtered;
  }, [accountsByTypeWithConverted, searchQuery, typeFilter]);

  // Handle loading state
  if (metricsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded mt-2" />
          </div>
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="h-64 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
        <div className="h-96 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  // Handle error state
  if (metricsError) {
    return (
      <ErrorFallback
        error={metricsError}
        title="Failed to Load Accounts"
        description="Unable to load your account data. Please check your connection and try again."
      />
    );
  }

  // Handle empty state (no accounts)
  if (!metrics || metrics.accountsWithConvertedBalances.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Account Overview
            </h1>
            <p className="text-muted-foreground">
              Account summary and distribution
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Dialog
            open={dialogOpen}
            onOpenChange={(v) => {
              setDialogOpen(v);
              if (!v) setEditing(undefined);
            }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg w-[95vw] sm:w-full">
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit Account" : "New Account"}
                </DialogTitle>
              </DialogHeader>
              <AccountForm
                account={editing}
                onSave={handleSave}
                onCancel={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="rounded-lg border bg-card p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-muted p-4">
              <Plus className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">No accounts yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Get started by adding your first account to track your finances
              </p>
            </div>
            <Button onClick={handleAddAccount} className="mt-2">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Add Your First Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Loading overlay during initial conversion */}
      {isConverting && metricsLoading && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
          role="alert"
          aria-busy="true"
          aria-live="assertive">
          <div className="text-center space-y-2">
            <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">
              Converting currencies...
            </p>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Account Overview
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Account summary and distribution
          </p>
        </div>
        <Button
          onClick={handleAddAccount}
          className="min-h-[44px] w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Add Account
        </Button>
      </div>

      <div className="flex justify-end">
        <Dialog
          open={dialogOpen}
          onOpenChange={(v) => {
            setDialogOpen(v);
            if (!v) setEditing(undefined);
          }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>
                {editing ? "Edit Account" : "New Account"}
              </DialogTitle>
            </DialogHeader>
            <AccountForm
              account={editing}
              onSave={handleSave}
              onCancel={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Currency Toggle - Prominent position near totals */}
      <div className="flex items-center gap-4">
        <CurrencyToggle
          baseCurrency={baseCurrency}
          selectedCurrency={selectedCurrency}
          onCurrencyChange={setSelectedCurrency}
          disabled={isConverting}
          showBalance={false}
        />
        {isConverting && (
          <span className="text-sm text-muted-foreground">Converting...</span>
        )}
      </div>

      {/* Error handling UI for conversion failures */}
      {conversionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Unable to convert currency. Please check your connection and try
            again.
          </AlertDescription>
        </Alert>
      )}

      {/* Live region for dynamic updates */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true">
        {isConverting && "Converting currency, please wait"}
        {!isConverting &&
          !metricsLoading &&
          `Portfolio loaded with ${convertedAccounts.length} accounts in ${selectedCurrency}`}
        {conversionError && "Currency conversion failed"}
      </div>

      {/* Portfolio Summary and Distribution */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <PortfolioSummaryCard
          totalNetWorth={totalNetWorth}
          baseCurrency={baseCurrency}
          selectedCurrency={selectedCurrency}
          accountsByType={metrics.accountsByType}
          isLoading={metricsLoading}
          isConverting={isConverting}
        />
        <AccountDistributionChart
          accounts={convertedAccounts}
          baseCurrency={baseCurrency}
          selectedCurrency={selectedCurrency}
          accountColors={accountColors}
          isLoading={metricsLoading}
        />
      </div>

      {/* Portfolio Balance Trend */}
      <PortfolioBalanceTrendChart
        title="Account"
        data={balanceTrend || []}
        currency={selectedCurrency}
        timePeriod={timePeriod}
        isLoading={trendLoading}
      />

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mt-8 mb-4">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.keys(accountsByTypeWithConverted).map((type) => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Account List by Type */}
      <AccountListByType
        accountsByType={filteredAccountsByType}
        baseCurrency={baseCurrency}
        selectedCurrency={selectedCurrency}
        isLoading={metricsLoading}
      />
    </div>
  );
}

export default function AccountOverview() {
  return (
    <ErrorBoundary>
      <AccountOverviewContent />
    </ErrorBoundary>
  );
}
